import { clamp, hash01, hash3 } from '@/core/rng';

/**
 * Culture (Design Expansion II §45, §50).
 *
 * A culture is not a band, a settlement, a religion or a polity, and the
 * whole point of it being its own object is that it outlives all of them. A
 * band splits and both halves are still the same people for generations. A
 * band dies and its culture goes on in its neighbours. Two communities a
 * hundred kilometres apart share a language for a century and then, slowly,
 * do not.
 *
 * What made this necessary rather than tidy: fission was already producing
 * divergence by reading a daughter's traits off whoever walked out, but
 * band traits are four numbers with clamp floors, so after forty-five years
 * the spread across two dozen bands sat at *exactly* 0.20 and stopped
 * widening. Divergence needs somewhere to keep going. This is that place —
 * a culture drifts on its own clock, forever, and bands are born holding
 * whatever their culture believed at the moment they were founded.
 *
 * Deliberately NOT here: religion. §53 is explicit that a culture may hold
 * one religion, several, or none, and that people can practise more than one
 * at once. Putting a religionId here would quietly make religion a property
 * of ethnicity, which is exactly the modelling mistake the doc names.
 */

/** Syllables for a people's name for itself. Not a language — a label. */
const HEAD = ['Ash', 'Riv', 'Stone', 'Elk', 'Fen', 'Kel', 'Mor', 'Tor', 'Vash',
              'Old', 'Bray', 'Corr', 'Dun', 'Hal', 'Nim', 'Sel'] as const;
const TAIL = ['folk', 'kin', 'people', 'born', 'wood', 'water', 'hearth',
              'walkers', 'holders', 'children'] as const;

export function cultureName(seed: number, idx: number): string {
  const a = HEAD[hash3(seed, idx, 0x7c1) % HEAD.length];
  const b = TAIL[hash3(seed, idx, 0x7c2) % TAIL.length];
  return `${a}${b}`;
}

/**
 * What a people leans toward, 0..1 each. These are the *inheritance*: a band
 * founded into a culture starts from them, and a culture that drifts carries
 * its descendants with it. They are wide open — no clamp floor at 0.05 — so
 * that a thousand years actually goes somewhere.
 */
export interface CultureLeanings {
  curiosity: number;
  caution: number;
  mobility: number;
  tradition: number;
}

/**
 * The concrete half of §50 — what a people actually *does*, as against what
 * it leans toward.
 *
 * Leanings are four numbers that drift, and after four centuries the most
 * they can say is that one people is 0.2 more cautious than another. Nobody
 * has ever looked at a hillside and seen caution. Practices are the half of
 * culture that can be *seen*: these people build round and those build long,
 * these lay their dead in the earth and those leave them out, this valley
 * cuts chevrons into its posts and the next one cuts spirals.
 *
 * Three rules make them behave like culture rather than like decoration:
 *
 * - **Discrete, not continuous.** A practice is one of a handful of named
 *   variants, so two peoples either do the same thing or they visibly do
 *   not. There is no 0.03 of a burial rite.
 * - **Inherited whole.** A daughter people carries its parent's practices
 *   out of the door with it, which is what makes "a band splits and both
 *   halves are still the same people for generations" (§50) true rather
 *   than a slogan. Divergence is what happens afterwards, slowly.
 * - **Observable, never doctrinal.** A practice records what a people is
 *   seen to do with a body, a roof or a wall — never what it thinks is true.
 *   §53 keeps religion off culture entirely, and `test/culture.ts` enforces
 *   that by scanning this system's own snapshot, so the *values* here have
 *   to stay behavioural. "They leave their dead on high ground" is a
 *   practice; why they do it is not culture's business.
 */
export const PRACTICES = {
  /** A dialect label, not a language: what these people sound like. */
  language: ['lilting', 'clipped', 'rolling', 'hushed'],
  /** What they mainly live on — the visible half of subsistence. */
  food: ['foragers', 'fishers', 'hunters', 'seed-gatherers'],
  /** The shape of a roof. The most legible of the six from the air. */
  building: ['round', 'longhouse', 'sunken', 'lean-to'],
  /** What is done with the dead. Observable behaviour, never a reason. */
  burial: ['in earth', 'to fire', 'left high', 'under stones'],
  /** The story everybody in this people knows. */
  story: ['the flood', 'the first fire', 'the long walk', 'the two brothers'],
  /** What they cut into posts, stones and skin. */
  symbol: ['chevron', 'spiral', 'handprint', 'open circle'],
} as const;

export type PracticeKind = keyof typeof PRACTICES;

export const PRACTICE_KINDS = Object.keys(PRACTICES) as PracticeKind[];

/** One variant of each — everything a people is seen to do. */
export type Practices = {
  [K in PracticeKind]: (typeof PRACTICES)[K][number];
};

/**
 * The roof shape, named on its own because it is the one practice that leaves
 * something standing. A structure records the shape it was *raised* in and
 * keeps it for ever — §30 deletes `ownerBandId` when a band dies out or moves
 * on, so a ruin has no culture to ask, and a shape derived from the living
 * owner would make a dead people's longhouses quietly turn round. Archaeology
 * is frozen: what is on the ground is what those hands did.
 */
export type BuildingPractice = Practices['building'];

export interface CultureState {
  id: string;
  name: string;
  /** The culture this one drifted out of, if it did. History, not a pointer. */
  parentId?: string;
  foundedAt: number;
  leanings: CultureLeanings;
  /** What this people is seen to do. Inherited whole, then slowly its own. */
  practices: Practices;
  /**
   * How far this culture has drifted since it was founded, accumulated. It
   * is what decides when a separated branch has become its own people.
   */
  drift: number;
}

/**
 * Drift per world-year, as a fraction of the range. Slow on purpose: a
 * culture is supposed to be the thing that changes least, so that a band
 * splitting off and remaining "the same people for generations" (§50) is
 * true rather than a slogan.
 */
const DRIFT_PER_YEAR = 0.035;
const YEAR = 365 * 86400;

/**
 * The chance a people changes one of its six practices in a given year, at
 * tradition 0.5. Practices are supposed to be the *slowest* thing about a
 * culture — much slower than leanings, which move every year — because a
 * people whose roofs change shape every decade is not a people.
 *
 * Tuned against `tools/practices.ts`, which runs the real thing: this rate
 * leaves a daughter people still recognisably its parent for the first few
 * generations, and visibly its own by the time §4's century of prehistory
 * has run. See ROADMAP §7 for the table.
 */
const INNOVATION_PER_YEAR = 0.003;

/**
 * How much tradition slows it. At tradition 1 a people innovates at a fifth
 * of the base rate; at 0 it is nearly double. This is the one place the
 * leanings do concrete work — before practices existed they were four
 * numbers that only ever fed back into themselves.
 */
const innovationRate = (tradition: number) =>
  INNOVATION_PER_YEAR * (1.8 - 1.6 * clamp(tradition, 0, 1));

export interface CultureSnapshot {
  cultures: CultureState[];
  nextId: number;
}

/** The practices a people is born doing — its own hash, nothing inherited. */
function foundingPractices(idx: number, seed: number): Practices {
  const out = {} as Record<PracticeKind, string>;
  PRACTICE_KINDS.forEach((kind, k) => {
    const options = PRACTICES[kind];
    out[kind] = options[hash3(idx, k * 131 + 7, seed ^ 0x5ac1) % options.length];
  });
  return out as Practices;
}

export class CultureSystem {
  private readonly list: CultureState[] = [];
  private nextId = 0;

  constructor(private readonly seed: number) {}

  all(): readonly CultureState[] {
    return this.list;
  }

  get(id: string | undefined): CultureState | undefined {
    return id === undefined ? undefined : this.list.find((c) => c.id === id);
  }

  /** A people, newly named. Leanings come from the founder's own hash. */
  found(now: number, from?: CultureState): CultureState {
    const idx = this.nextId++;
    const rng = (k: number) => hash01(idx, k, this.seed ^ 0x3f1);
    const c: CultureState = {
      id: `culture-${idx}`,
      name: cultureName(this.seed, idx),
      foundedAt: now,
      leanings: from
        ? { ...from.leanings }
        : {
            curiosity: 0.25 + rng(1) * 0.5,
            caution: 0.25 + rng(2) * 0.5,
            mobility: 0.25 + rng(3) * 0.5,
            tradition: 0.25 + rng(4) * 0.5,
          },
      // A daughter people walks out doing exactly what its parent did. That
      // is the whole of §50's "still the same people for generations" — the
      // divergence is what the years do to it afterwards, not the split.
      practices: from ? { ...from.practices } : foundingPractices(idx, this.seed),
      drift: 0,
    };
    if (from) c.parentId = from.id;
    this.list.push(c);
    return c;
  }

  /**
   * A year of a culture's own slow change. Deterministic from the culture's
   * identity and the year it is — no shared stream, so the same world drifts
   * the same way whatever else happened first.
   */
  drift(from: number, to: number): void {
    const y0 = Math.floor(from / YEAR), y1 = Math.floor(to / YEAR);
    if (y1 <= y0) return;
    for (let y = y0 + 1; y <= y1; y++) {
      for (const c of this.list) {
        const step = (k: number) =>
          (hash01(Number(c.id.slice(8)) + k * 977, y, this.seed ^ 0x9d3) - 0.5)
          * 2 * DRIFT_PER_YEAR;
        const l = c.leanings;
        const before = { ...l };
        l.curiosity = clamp(l.curiosity + step(1), 0, 1);
        l.caution = clamp(l.caution + step(2), 0, 1);
        l.mobility = clamp(l.mobility + step(3), 0, 1);
        l.tradition = clamp(l.tradition + step(4), 0, 1);
        c.drift += Math.abs(l.curiosity - before.curiosity)
          + Math.abs(l.caution - before.caution)
          + Math.abs(l.mobility - before.mobility)
          + Math.abs(l.tradition - before.tradition);

        // and the rare year in which a people starts doing something else.
        // Rolled per practice from the culture's own identity and the year,
        // so this rides the same absolute grid the leanings do and a split
        // advance innovates in exactly the same years as a whole one.
        const idx = Number(c.id.slice(8));
        const rate = innovationRate(l.tradition);
        PRACTICE_KINDS.forEach((kind, k) => {
          if (hash01(idx + k * 4093, y * 31 + 11, this.seed ^ 0x2b7d) >= rate) return;
          const options = PRACTICES[kind];
          // move to a *different* variant: an innovation nobody can see is
          // not an innovation, so the new one is drawn from the others only
          const others = options.filter((o) => o !== c.practices[kind]);
          const pick = hash3(idx + k * 4093, y, this.seed ^ 0x6e2f) % others.length;
          (c.practices as Record<PracticeKind, string>)[kind] = others[pick];
        });
      }
    }
  }

  /**
   * How differently two peoples live, 0 identical .. 1 nothing in common —
   * the fraction of the six practices on which they visibly differ.
   *
   * Kept separate from `distance()` rather than folded into it. That one
   * measures leanings, it is what the divergence machinery was tuned
   * against, and quietly changing what it means would move numbers §3 and
   * §7 were measured with. This is the readable half, and it is the one a
   * person standing in the next valley would actually notice.
   */
  practiceDistance(a: CultureState, b: CultureState): number {
    let differ = 0;
    for (const kind of PRACTICE_KINDS) {
      if (a.practices[kind] !== b.practices[kind]) differ++;
    }
    return differ / PRACTICE_KINDS.length;
  }

  /** How unlike each other two peoples have become, 0 identical .. 1 alien. */
  distance(a: CultureState, b: CultureState): number {
    return clamp(
      (Math.abs(a.leanings.curiosity - b.leanings.curiosity) +
       Math.abs(a.leanings.caution - b.leanings.caution) +
       Math.abs(a.leanings.mobility - b.leanings.mobility) +
       Math.abs(a.leanings.tradition - b.leanings.tradition)) / 4, 0, 1);
  }

  toSnapshot(): CultureSnapshot {
    return {
      nextId: this.nextId,
      cultures: this.list.map((c) => ({
        ...c, leanings: { ...c.leanings }, practices: { ...c.practices },
      })),
    };
  }

  restore(s: CultureSnapshot): void {
    this.list.length = 0;
    for (const c of s.cultures) {
      this.list.push({ ...c, leanings: { ...c.leanings }, practices: { ...c.practices } });
    }
    this.nextId = s.nextId;
  }
}
